import { useEffect, useRef, type ReactNode } from 'react'
import { useCrisisStore } from '../stores/crisisStore'
import { useAuth } from './useAuth'
import { websocketService } from '../services/websocketService'

interface CrisisProviderProps {
  children: ReactNode
}

export function CrisisProvider({ children }: CrisisProviderProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const { refreshActiveCrisis, activeCrisis, initState, setInitState, clearActiveCrisis } = useCrisisStore()
  const hasInitializedRef = useRef(false)

  useEffect(() => {
    // Initialize crisis state once when auth is ready and authenticated
    if (isAuthenticated && !authLoading && !hasInitializedRef.current) {
      hasInitializedRef.current = true
      console.debug('[CrisisProvider] Auth ready, initializing crisis state')

      // If crisis was restored from persistence, mark as ready immediately
      if (activeCrisis && initState === 'idle') {
        console.debug('[CrisisProvider] Active crisis restored from persistence, marking ready')
        setInitState('ready')
        return
      }

      // Otherwise fetch active crisis (session logic is now in store)
      if (initState === 'idle') {
        console.debug('[CrisisProvider] Fetching active crisis')
        refreshActiveCrisis()
      }
    }
  }, [isAuthenticated, authLoading, refreshActiveCrisis, activeCrisis, initState, setInitState])

  // Connect/disconnect WebSocket based on auth state so crisis_closed (and other
  // real-time events) are always received, regardless of which page the user is on.
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      websocketService.connect().catch(err => {
        console.warn('[CrisisProvider] WebSocket connection failed:', err)
      })
    } else if (!isAuthenticated && !authLoading) {
      websocketService.disconnect()
    }
  }, [isAuthenticated, authLoading])

  // Handle logout: reset initialization when auth state changes to false
  useEffect(() => {
    if (!isAuthenticated && !authLoading) {
      hasInitializedRef.current = false
    }
  }, [isAuthenticated, authLoading])

  // Auto-leave active crisis when it is closed by another user via WebSocket
  useEffect(() => {
    const unsubscribe = websocketService.onCrisisClosed(({ crisis_id }) => {
      const { activeCrisis: current } = useCrisisStore.getState()
      if (current?.id === crisis_id) {
        console.debug('[CrisisProvider] Active crisis was closed remotely, clearing', crisis_id)
        clearActiveCrisis()
      }
    })
    return unsubscribe
  }, [clearActiveCrisis])

  return <>{children}</>
}
